import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { message, Tooltip } from 'antd'
import { motion } from 'framer-motion'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { Building, Floor } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import BusinessSettingIcon from 'components/icons/BusinessSettingIcon'
import ChevronDownIcon from 'components/icons/ChevronDownIcon'
import SettingLocation from 'components/icons/SettingLocation'
import ShowSubImageArrown from 'components/icons/ShowSubImageArrown'
import ZoomIcon from 'components/icons/ZoomIcon'

import useDragScroll from '../../hook/Carousel/useDragScroll'
import useThumbnailCarousel from '../../hook/Carousel/useThumbnailCarousel'
import { SubImageOverlay } from '../type'

import SubImageThubnailCarousel from './SubImageThubnailCarousel'

import './thumbnailCarousel.css'

// Extend TypeScript interfaces for fullscreen APIs
declare global {
  interface HTMLElement {
    webkitRequestFullscreen?: () => Promise<void>
    msRequestFullscreen?: () => Promise<void>
    webkitEnterFullscreen?: () => Promise<void>
    mozRequestFullScreen?: () => Promise<void>
  }

  interface Document {
    webkitExitFullscreen?: () => Promise<void>
    msExitFullscreen?: () => Promise<void>
    mozCancelFullScreen?: () => Promise<void>
    webkitFullscreenElement?: Element | null
    msFullscreenElement?: Element | null
    mozFullScreenElement?: Element | null
    fullscreenElement?: Element | null
    webkitIsFullScreen?: boolean
    mozFullScreen?: boolean
    msFullscreenEnabled?: boolean
    webkitFullscreenEnabled?: boolean
    mozFullScreenEnabled?: boolean
  }
}

function ThumbnailCarousel({
  setSelectedPointID,
  building,
  floors,
  setOpenBusinessInfo,
  setOpenMapLocation
}: {
  setOpenBusinessInfo: Dispatch<SetStateAction<boolean>>
  setOpenMapLocation: Dispatch<SetStateAction<boolean>>
  setSelectedPointID: Dispatch<SetStateAction<string>>
  building: Building | undefined
  floors: Floor[] | undefined
}) {
  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const thumbnailRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const matches = useMediaQuery('(min-width: 768px)')
  const [toolTipOpen, settoolTipOpen] = useState({
    floor: false,
    image: false
  })
  const [currentSubImageCarouselOpen, setCurrentSubImageCarouselOpen] =
    useState({
      isOpen: false,
      pointId: undefined
    } as {
      isOpen: boolean
      pointId?: string
    })

  // state of sub-image overlay
  const [subImageOverlay, setSubImageOverlay] = useState<SubImageOverlay>({
    isOpen: false,
    pointId: '',
    top: 0,
    left: 0,
    width: 0
  })

  const {
    isOpen,
    setIsOpen,
    carouselRef,
    handleThumbnailClick,
    handleSelectSubImage
  } = useThumbnailCarousel({
    setSelectedPointID,
    building
  })

  const mainCarousel = useDragScroll(2)
  const subImageCarousel = useDragScroll(3, subImageOverlay.isOpen)

  const handleToggleSubImages = (pointId: string) => {
    // If open and click same point, close
    if (subImageOverlay.isOpen && subImageOverlay.pointId === pointId) {
      setSubImageOverlay({
        isOpen: false,
        pointId: '',
        top: 0,
        left: 0,
        width: 0
      })
      return
    }

    // If open other point, close old point and open new point
    if (subImageOverlay.isOpen) {
      setSubImageOverlay({
        isOpen: false,
        pointId: '',
        top: 0,
        left: 0,
        width: 0
      })
      // Wait a bit then open point new
      setTimeout(() => {
        const el = thumbnailRefs.current[pointId]
        if (el) {
          const rect = el.getBoundingClientRect()
          setSubImageOverlay({
            isOpen: true,
            pointId,
            top: rect.top,
            left: rect.left - 5,
            width: rect.width
          })
        }
      }, 100)
      return
    }

    // Get position of thumbnail on screen
    const el = thumbnailRefs.current[pointId]
    if (el) {
      const rect = el.getBoundingClientRect()
      setSubImageOverlay({
        isOpen: true,
        pointId,
        top: rect.top,
        left: rect.left - 5,
        width: rect.width
      })
    }
  }

  // Close overlay when click outside
  useEffect(() => {
    if (!subImageOverlay.isOpen) return
    const handleClick = (e: MouseEvent) => {
      const overlay = document.getElementById('subimage-overlay')
      if (overlay && !overlay.contains(e.target as Node)) {
        setSubImageOverlay({
          isOpen: false,
          pointId: '',
          top: 0,
          left: 0,
          width: 0
        })
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [subImageOverlay.isOpen])

  const enterFullscreen = useCallback(async () => {
    const element = document.documentElement
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      } else if (element.webkitEnterFullscreen) {
        await element.webkitEnterFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else {
        message.warning('このブラウザは全画面表示をサポートしていません')
      }
    } catch (err) {
      message.error('全画面表示に切り替えられませんでした')
    }
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      }
    } catch (err) {
      // Silent error
    }
  }, [])

  const isFullscreen = useCallback(() => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    )
  }, [])

  const toggleFullScreen = useCallback(async () => {
    try {
      if (isFullscreen()) {
        await exitFullscreen()
      } else {
        await enterFullscreen()
      }
    } catch (error) {
      message.error('全画面表示の切り替え中にエラーが発生しました')
    }
  }, [enterFullscreen, exitFullscreen, isFullscreen])

  useEffect(() => {
    const activePointId = currentViewer.selectedPoint

    if (activePointId && carouselRef.current) {
      const selectedElement = carouselRef.current.querySelector(
        `[data-point-id="${activePointId}"]`
      ) as HTMLElement

      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        })
      }
    }
  }, [carouselRef, currentViewer.selectedPoint])

  useEffect(() => {
    if (
      currentViewer.selectedPoint &&
      currentViewer.selectedImage === currentViewer.pointDetail?.image360?.id
    ) {
      handleThumbnailClick(currentViewer.selectedPoint)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedImage])

  return (
    <div className="w-screen tour-view-page-carousel relative">
      {/* Action Bar - no background */}
      <div className="flex justify-between items-center w-[98%] mx-auto py-3">
        <div
          className={`flex gap-x-7 items-center ${
            !matches ? 'w-[70%]' : 'w-full'
          }`}
        >
          <div
            onClick={() => setIsOpen(!isOpen)}
            className="cursor-pointer w-[45px] h-[24px] flex items-center justify-center rounded-md border border-gray-300 transition active:opacity-80"
          >
            <div className="relative w-5 h-5 ">
              <ChevronDownIcon
                className={`absolute transition-all duration-300 ease-in-out transform ${
                  !isOpen ? 'rotate-0' : '-rotate-180'
                }
                ${!matches ? 'scale-75' : 'scale-100'}`}
              />
            </div>
          </div>

          <div
            className="flex flex-col ml-2 text-white"
            style={{ width: matches ? '70%' : '100%' }}
          >
            {/* Floor Name */}
            <Tooltip
              open={toolTipOpen.floor}
              title={
                floors?.find((f) => f.id === currentViewer.selectedFloor)
                  ?.name || ''
              }
              placement="top"
              color="#0E0D0A80"
              trigger={matches ? 'hover' : ['click']}
              overlayStyle={{ maxWidth: '80vw' }}
              mouseLeaveDelay={0}
              autoAdjustOverflow
              onOpenChange={(open) => {
                settoolTipOpen({
                  floor: open,
                  image: false
                })
                setTimeout(
                  () =>
                    settoolTipOpen({
                      floor: false,
                      image: false
                    }),
                  1500
                )
              }}
            >
              <div
                className="text-ellipsis overflow-hidden"
                style={{
                  fontSize: matches ? '14px' : '12px',
                  fontWeight: 'bold',
                  lineHeight: '1.4',
                  maxHeight: '2.8em',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  display: '-webkit-box',
                  wordBreak: 'break-word',
                  cursor: 'pointer'
                }}
              >
                {
                  floors?.find((f) => f.id === currentViewer.selectedFloor)
                    ?.name
                }
              </div>
            </Tooltip>

            {/* Image Name */}
            <Tooltip
              open={toolTipOpen.image}
              title={
                currentViewer.selectedImage ===
                currentViewer.pointDetail?.image360?.id
                  ? currentViewer.pointDetail?.image360?.name || ''
                  : currentViewer.pointDetail?.subImages?.find(
                      (s) => s.id === currentViewer.selectedImage
                    )?.name || ''
              }
              placement="top"
              color="#0E0D0A80"
              trigger={matches ? 'hover' : ['click']}
              overlayStyle={{ maxWidth: '80vw' }}
              mouseLeaveDelay={0}
              autoAdjustOverflow
              onOpenChange={(open) => {
                settoolTipOpen({
                  floor: false,
                  image: open
                })
                setTimeout(
                  () =>
                    settoolTipOpen({
                      floor: false,
                      image: false
                    }),
                  1500
                )
              }}
            >
              <div
                className="text-ellipsis overflow-hidden"
                style={{
                  fontSize: matches ? '24px' : '14px',
                  fontWeight: 'bold',
                  lineHeight: '1.3',
                  maxHeight: '2.6em',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  display: '-webkit-box',
                  wordBreak: 'break-word',
                  cursor: 'pointer',
                  marginTop: '2px'
                }}
              >
                {currentViewer.selectedImage ===
                currentViewer.pointDetail?.image360?.id
                  ? currentViewer.pointDetail?.image360?.name || ''
                  : currentViewer.pointDetail?.subImages?.find(
                      (s) => s.id === currentViewer.selectedImage
                    )?.name || ''}
              </div>
            </Tooltip>
          </div>
        </div>

        <div
          className={`flex justify-center items-center ${
            matches ? 'gap-x-8' : 'gap-x-2'
          }`}
        >
          <div
            onClick={() => setOpenBusinessInfo(true)}
            className="cursor-pointer"
          >
            <BusinessSettingIcon className={!matches ? 'scale-90' : ''} />
          </div>
          <div
            onClick={() => setOpenMapLocation(true)}
            className="cursor-pointer"
          >
            <SettingLocation className={!matches ? 'scale-90' : ''} />
          </div>
          <div onClick={toggleFullScreen} className="cursor-pointer">
            <ZoomIcon className={!matches ? 'scale-90' : ''} />
          </div>
        </div>
      </div>

      {/* Sub Image Carousel */}
      <SubImageThubnailCarousel
        subImageOverlay={subImageOverlay}
        handleClickSubImage={handleSelectSubImage}
        subImageCarousel={subImageCarousel}
        matches={matches}
      />

      {/* Carousel with background blur */}
      {isOpen && (
        <div className="bg-white/30 backdrop-blur-[2px] relative">
          <div
            className={`mx-auto opacity-100 relative ${
              matches ? 'max-h-[165px] w-[96%]' : 'max-h-[140px] w-full'
            }`}
          >
            <div
              ref={mainCarousel.containerRef}
              className={`flex overflow-x-auto no-scrollbar ${
                matches
                  ? 'h-[170px] space-x-6 px-8'
                  : 'h-[130px] space-x-3 px-4'
              } relative`}
              onMouseDown={mainCarousel.onMouseDown}
              onMouseMove={mainCarousel.onMouseMove}
              onMouseUp={mainCarousel.onMouseUp}
              onMouseLeave={mainCarousel.onMouseLeave}
              onTouchStart={mainCarousel.onTouchStart}
              onTouchMove={mainCarousel.onTouchMove}
              onTouchEnd={mainCarousel.onTouchEnd}
              onMouseEnter={mainCarousel.onMouseEnter}
              onScroll={() =>
                setSubImageOverlay({
                  isOpen: false,
                  pointId: '',
                  top: 0,
                  left: 0,
                  width: 0
                })
              }
              style={{
                cursor: mainCarousel.isDragging ? 'grabbing' : 'grab',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              {points
                .filter((p) => p.image360?.thumbnail)
                .sort((a, b) => a.order - b.order)
                .map((point) => {
                  const isSelected =
                    currentViewer.selectedImage === point.image360?.id

                  return (
                    <div
                      key={point.id}
                      className="flex flex-col justify-center items-center relative select-none"
                      ref={(el: HTMLDivElement | null) => {
                        thumbnailRefs.current[point.id] = el
                      }}
                    >
                      <div
                        onClick={() => {
                          if (
                            currentSubImageCarouselOpen.isOpen &&
                            currentSubImageCarouselOpen.pointId === point.id
                          ) {
                            setCurrentSubImageCarouselOpen((prev) => ({
                              ...prev,
                              isOpen: false,
                              pointId: undefined
                            }))
                            return
                          }

                          setCurrentSubImageCarouselOpen((prev) => ({
                            ...prev,
                            isOpen: true,
                            pointId: point.id
                          }))
                          if (currentViewer.selectedPoint !== point.id) {
                            handleThumbnailClick(point.id)
                          }

                          setTimeout(
                            () => {
                              handleToggleSubImages(point.id)
                            },
                            isSelected ? 0 : 1000
                          )
                        }}
                        className={`absolute ${
                          isSelected ? '-top-[2px]' : '-top-0'
                        } z-10 ${
                          point.isSubImages ? 'block' : 'hidden'
                        } p-1 transition cursor-pointer`}
                      >
                        <ShowSubImageArrown
                          className={`absolute transition-all duration-300 ease-in-out transform ${
                            subImageOverlay.isOpen &&
                            subImageOverlay.pointId === point.id
                              ? 'rotate-180'
                              : 'rotate-0'
                          }
                          ${!matches ? 'scale-75' : 'scale-100'}`}
                        />
                      </div>

                      <motion.div
                        data-point-id={point.id}
                        initial={{ scale: 1.0 }}
                        animate={{ scale: isSelected ? 1.08 : 1.0 }}
                        transition={{ duration: 0.1 }}
                        style={{
                          zIndex: isSelected ? 1 : 0,
                          width: matches ? '168px' : '120px',
                          height: matches ? '117px' : '90px',
                          flexShrink: 0,
                          opacity: 1,
                          willChange: 'transform, opacity'
                        }}
                        onClick={() => {
                          if (currentViewer.imageIsLoading) return

                          handleThumbnailClick(point.id)
                          setCurrentViewer((prev) => ({
                            ...prev,
                            selectedImage: point.image360?.id,
                            isClickFromTourCarousel: true
                          }))
                        }}
                        className={`relative overflow-hidden transition-all duration-150 ${
                          isSelected ? 'selected-thumbnail' : 'border-0'
                        } ${
                          currentViewer.imageIsLoading
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        }`}
                      >
                        {point.isSubImages && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-[2px] text-[10px] flex bg-white text-black items-center justify-center z-10 select-none">
                            {point.numberOfSubImages ||
                              (point.subImages ? point.subImages.length : 0)}
                          </div>
                        )}
                        <LazyThumbnail
                          imageType={ImageType.OBJECT_FILL}
                          url={
                            point.image360?.multires
                              ? `${point.image360?.tilesUrl}thumb.jpg`
                              : point.image360?.thumbnail || ''
                          }
                          className="w-full h-full"
                          name={point.image360?.name || ''}
                          width={168}
                          height={117}
                        />
                        <Tooltip
                          title={point.image360?.name}
                          placement="top"
                          color="#0E0D0A80"
                          trigger="hover"
                        >
                          <div className="absolute bottom-0 select-none left-0 px-2 right-0 bg-black bg-opacity-50 text-white text-[13px] font-bold p-1 text-center overflow-ellipsis whitespace-nowrap overflow-hidden">
                            {point.image360?.name}
                          </div>
                        </Tooltip>
                      </motion.div>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ThumbnailCarousel
